import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import dynamic from 'next/dynamic';

import { useAppliedOrgEmployee } from 'components/Globals/FormFields/Display/OrgEmployeeDropdown';

import { OB_SALES_DEMO_ACCOUNT } from 'constants/cookieConstants';

import { getCookie, removeCookie, setCookie } from 'utils/cookie';

import usePageContext from './usePageContext';

const SalesDemoForOrg = dynamic(() => import('components/Globals/SalesDemoForOrg'), { ssr: false });

const SalesDemoForOrgContext = createContext({});

const useSavedSalesDemoContext = () => {
  const savedSalesDemoAccount = getCookie(OB_SALES_DEMO_ACCOUNT);

  if (!savedSalesDemoAccount) {
    return null;
  }

  const [orgId, id] = savedSalesDemoAccount.split('-');

  return {
    orgId,
    id,
  };
};

export const SalesDemoForOrgContextProvider = ({ children }) => {
  const { permissions } = usePageContext();
  const savedSalesDemoAccount = useSavedSalesDemoContext();
  const [showSalesDemoModal, setShowSalesDemoModal] = useState(false);
  const isSalesDemoForOrgAllowed = useMemo(() => permissions?.isAdmin, [permissions?.isAdmin]);

  const [salesDemoAccount, setSalesDemoAccount] = useState(() => {
    if (isSalesDemoForOrgAllowed && savedSalesDemoAccount?.id) {
      const { id, orgId } = savedSalesDemoAccount;
      return { organization: { id: orgId }, id };
    }

    return null;
  });

  const setSalesDemoAccountInfo = useCallback(
    ({ id, orgId } = {}) => {
      setSalesDemoAccount(() => {
        if (isSalesDemoForOrgAllowed && id) {
          return {
            id,
            organization: { id: orgId },
          };
        }

        return null;
      });
    },
    [isSalesDemoForOrgAllowed],
  );

  useEffect(() => {
    if (salesDemoAccount?.id) {
      setCookie(OB_SALES_DEMO_ACCOUNT, `${salesDemoAccount?.organization?.id}-${salesDemoAccount?.id}`);
    } else {
      removeCookie(OB_SALES_DEMO_ACCOUNT);
    }
  }, [salesDemoAccount]);

  const { data: salesDemoAccountInfo } = useAppliedOrgEmployee(salesDemoAccount, {
    enabled: isSalesDemoForOrgAllowed && salesDemoAccount?.id,
  });

  const context = useMemo(
    () => ({
      isSalesDemoForOrgAllowed,
      isSalesDemoMode: isSalesDemoForOrgAllowed && !!salesDemoAccount?.id,
      salesDemoAccountInfo: isSalesDemoForOrgAllowed ? salesDemoAccountInfo : null,
      salesDemoUserId: isSalesDemoForOrgAllowed ? salesDemoAccountInfo?.profile?.user?.id : null,
      salesDemoOrgId: isSalesDemoForOrgAllowed ? salesDemoAccountInfo?.organization?.id : null,
      showSalesDemoModal,
      setShowSalesDemoModal,
    }),
    [isSalesDemoForOrgAllowed, salesDemoAccount, salesDemoAccountInfo, showSalesDemoModal, setShowSalesDemoModal],
  );

  return (
    <SalesDemoForOrgContext.Provider value={context}>
      <>
        {children}
        {isSalesDemoForOrgAllowed && showSalesDemoModal && (
          <SalesDemoForOrg
            salesDemoAccountInfo={isSalesDemoForOrgAllowed ? salesDemoAccountInfo : null}
            onSubmit={setSalesDemoAccountInfo}
            onCancel={() => setSalesDemoAccountInfo()}
            onClose={() => setShowSalesDemoModal(false)}
            isOpen
          />
        )}
      </>
    </SalesDemoForOrgContext.Provider>
  );
};

const useSalesDemoForOrgContext = () => useContext(SalesDemoForOrgContext);

export default useSalesDemoForOrgContext;
